/* eslint-disable @next/next/no-img-element */
import React, { useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { AptunoButton, Icon, SIZES, useResponsive, VARIANTS } from '@aptuno/aptuno-ui';
import { ActionsBarRoot, BigTitle, HeaderSection, ImageWrapper, TextNormal } from '../EstateApplication.styled';
import { ApplicationInProgressWrapper } from './ApplicationInProgressScreen.styled';
import { Response, UserContext } from '@contexts/Shared';
import {
  ApplicationStateSteps,
  estateApplicationStageContext,
  useCreditStudyActions,
  useDocumentationActions,
} from '@contexts/Estate';
import { useTrackingSource } from '@contexts/Shared/utils';
import { useAnalyticsTracker } from '@services/analytics';
import { ApplicationResumed } from '@contexts/Estate/EstateApplication/domain/events/application-resumed.event';

export const ApplicationInProgressScreen = () => {
  const { isMobile } = useResponsive();
  const router = useRouter();
  const user = useContext(UserContext);
  const estateApplication = useContext(estateApplicationStageContext);
  const eventTracker = useAnalyticsTracker();

  const originSource = useTrackingSource();

  const { result: providedUserInformation, get: getProvidedUserInformation } =
    useCreditStudyActions().creditStudyQueryActions.useGetProvidedUserInformation();
  const {
    result: exposedDocumentation,
    loading: isLoadingExposedDocuments,
    get: getExposedDocumentation,
  } = useDocumentationActions().documentQueryActions.useGetExposedDocumentation();

  useEffect(() => {
    if (router.query && router.query.estateLegalId) {
      router.push({
        pathname: `/aplicar-inmueble/${router.query.estateLegalId}`,
        query: { ...router.query, stage: 'application-in-progress' },
      });
    }
  }, [router.query.estateLegalId]);

  useEffect(() => {
    if (user && user.token) getProvidedUserInformation();
  }, [user]);

  useEffect(() => {
    if (Response.isSucceed(providedUserInformation) && Response.isNotPerformed(exposedDocumentation)) {
      getExposedDocumentation({
        creditStudyId: providedUserInformation.result.creditStudyId,
      });
    }
  }, [providedUserInformation.type, providedUserInformation.type]);

  const handleNextStepClick = () => {
    if (isCompleteApplication) {
      router.push('/perfil');
    } else {
      segmentTrack();
      estateApplication && estateApplication.setCurrentState(ApplicationStateSteps.documentsUpload);
    }
  };

  const handleOnBackClick = () => {
    router.back();
  };

  const segmentTrack = () =>
    user?.data?.id && eventTracker.trackEvent(new ApplicationResumed(originSource, user.data.id));

  const isExposedDocumentationLoaded = typeof exposedDocumentation === 'undefined' && !isLoadingExposedDocuments;
  const isCompleteApplication =
    Response.isSucceed(exposedDocumentation) && exposedDocumentation.result.isComplete.value;

  return (
    <ApplicationInProgressWrapper isMobile={isMobile}>
      <HeaderSection horizontalAlign="center" verticalAlign="center" padding={'0'}>
        <ImageWrapper height={`222px`} width={`164px`} margin={`40px 0 48px 0`}>
          <img src={`/images/retomar.svg`} alt="Application in progress" />
        </ImageWrapper>
        <BigTitle>
          {isCompleteApplication ? 'Estamos procesando tu aplicación' : 'Ya tienes una aplicación en proceso'}
        </BigTitle>
        <TextNormal align="center">
          {isCompleteApplication
            ? 'Te escribiremos por WhatsApp una vez tengamos tu resultado.'
            : 'Termínala y una vez tengas el resultado, podrás utilizarlo para aplicar a otros inmuebles.'}
        </TextNormal>
      </HeaderSection>
      <ActionsBarRoot>
        <AptunoButton appearance={VARIANTS.SECONDARY} size={SIZES.MEDIUM} onClick={handleOnBackClick}>
          <Icon width={'12px'} height={'12px'} icon="arrowleftalt" />
          Regresar
        </AptunoButton>
        <AptunoButton
          disabled={isExposedDocumentationLoaded}
          appearance={VARIANTS.PRIMARY}
          size={SIZES.BIGGEST}
          onClick={handleNextStepClick}
        >
          {!isCompleteApplication ? 'Retomar aplicación' : 'Ir a mi perfil'}
          <Icon width={'12px'} height={'12px'} icon="Arrow_Right" />
        </AptunoButton>
      </ActionsBarRoot>
    </ApplicationInProgressWrapper>
  );
};
